import React, { useState, useEffect } from "react";
import {
    Bell,
    Check,
    CheckCircle,
    Clock,
    ShieldAlert,
    AlertCircle,
    Info,
    Trash2,
    Activity,
    User,
    FileText,
    Settings
} from "lucide-react";
import { superAdminAPI } from "../../api/apiService";
import { formatDistanceToNow } from "date-fns";

export default function SystemNotificationsPage() {
    const [notifications, setNotifications] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loadingNotifications, setLoadingNotifications] = useState(true);
    const [loadingActivities, setLoadingActivities] = useState(true);
    const [filter, setFilter] = useState("all"); // all, unread, read

    const fetchNotifications = async () => {
        try {
            setLoadingNotifications(true);
            const params = {};
            if (filter === "unread") params.is_read = false;
            if (filter === "read") params.is_read = true;

            const res = await superAdminAPI.getNotifications(params);
            setNotifications(res.data);
        } catch (error) {
            const isSuspended =
                error?.code === 'ERR_NETWORK_IO_SUSPENDED' ||
                error?.code === 'ERR_NETWORK' ||
                error?.code === 'ERR_CANCELED' ||
                error?.message?.includes('Network Error') ||
                error?.message?.includes('suspended');
            if (!isSuspended && error?.response?.status !== 403) {
                console.error("Error fetching notifications", error);
            }
        } finally {
            setLoadingNotifications(false);
        }
    };

    const fetchActivities = async () => {
        try {
            setLoadingActivities(true);
            // Assuming getActivityLogs exists as per apiService inspection
            const res = await superAdminAPI.getActivityLogs({ limit: 10 });
            setActivities(res.data || []);
        } catch (error) {
            console.error("Error fetching activities", error);
            // Fallback mock data if API fails or doesn't exist yet
            setActivities([
                { id: 1, action: "User Login", user: "Admin", details: "Logged in from 192.168.1.1", created_at: new Date().toISOString(), type: "login" },
                { id: 2, action: "Settings Update", user: "Super Admin", details: "Updated tax configuration", created_at: new Date(Date.now() - 3600000).toISOString(), type: "settings" },
            ]);
        } finally {
            setLoadingActivities(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, [filter]);

    useEffect(() => {
        fetchActivities();
    }, []);

    const handleMarkRead = async (id) => {
        try {
            await superAdminAPI.markNotificationRead(id);
            // Optimistic update
            setNotifications(notifications.map(n =>
                n.id === id ? { ...n, is_read: true } : n
            ));
        } catch (error) {
            console.error("Error marking notification read", error);
            fetchNotifications(); // Revert on error
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await superAdminAPI.markAllNotificationsRead();
            setNotifications(notifications.map(n => ({ ...n, is_read: true })));
            fetchNotifications();
        } catch (error) {
            console.error("Error marking all read", error);
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity) {
            case "CRITICAL": return <ShieldAlert className="w-5 h-5 text-red-500" />;
            case "WARNING": return <AlertCircle className="w-5 h-5 text-yellow-500" />;
            default: return <Info className="w-5 h-5 text-blue-500" />;
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case "CRITICAL": return "bg-red-50 border-red-100 text-red-700 dark:bg-red-900/20 dark:border-red-900/50 dark:text-red-400";
            case "WARNING": return "bg-yellow-50 border-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-900/50 dark:text-yellow-400";
            default: return "bg-blue-50 border-blue-100 text-blue-700 dark:bg-blue-900/20 dark:border-blue-900/50 dark:text-blue-400";
        }
    };

    // const getActivityIcon = (type) => { // Removed
    //     switch (type) {
    //         case 'login': return <User className="w-4 h-4 text-green-500" />;
    //         case 'settings': return <Settings className="w-4 h-4 text-orange-500" />;
    //         default: return <Activity className="w-4 h-4 text-slate-500" />;
    //     }
    // }; // Removed

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">System Notifications</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">Manage and view system notifications and updates</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={handleMarkAllRead}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
                    >
                        <CheckCircle className="w-4 h-4" />
                        Mark All Read
                    </button>
                    <button
                        onClick={fetchNotifications}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 pb-1">
                {["all", "unread", "read"].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setFilter(tab)}
                        className={`px-4 py-2 text-sm font-medium rounded-t-lg transition relative ${filter === tab
                            ? "text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400"
                            : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                            }`}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {/* Notification List */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                {loadingNotifications ? (
                    <div className="p-8 text-center text-slate-500">Loading notifications...</div>
                ) : notifications.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Bell className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">No notifications found</h3>
                        <p className="text-slate-500 dark:text-slate-400">You're all caught up!</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                        {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${!notification.is_read ? "bg-slate-50/50 dark:bg-slate-800/80" : ""
                                    }`}
                            >
                                <div className="flex gap-4">
                                    <div className="mt-1 flex-shrink-0">
                                        {getSeverityIcon(notification.severity)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <h4 className={`text-sm font-semibold ${!notification.is_read ? "text-slate-900 dark:text-white" : "text-slate-700 dark:text-slate-300"
                                                }`}>
                                                {notification.title}
                                            </h4>
                                            <span className="text-xs text-slate-400 whitespace-nowrap ml-2 flex-shrink-0">
                                                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                            </span>
                                        </div>
                                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed break-words">
                                            {notification.message}
                                        </p>

                                        <div className="mt-3 flex items-center gap-3">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded textxs font-medium ${getSeverityColor(notification.severity)}`}>
                                                {notification.severity}
                                            </span>

                                            {!notification.is_read && (
                                                <button
                                                    onClick={() => handleMarkRead(notification.id)}
                                                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 flex items-center gap-1"
                                                >
                                                    <Check className="w-3 h-3" />
                                                    Mark as Read
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
